/**
 * "Found along the way" (ENG-2966): what Reeve found while planning that the
 * ticket did not ask for, kept off the plan. Read off the plan page's own
 * section, the one record of them, and folded under the plan: one line,
 * "Found along the way · N", until a person opens it. Each row has its own
 * Create ticket, which makes a ticket of it on the same Team
 * (`POST /api/work-items/:id/follow-ups`), and then is that ticket's key.
 */
import { useState } from "react";
import { readPlanFollowUpsSection, workItemUrl } from "@yagni/shared";
import { Btn, Chip, Fold } from "../queue/index.js";

export interface PlanFollowUpsProps {
  /** The plan page as written, its "Found along the way" section included. */
  pageContent: string | null | undefined;
  /** The Team the made tickets are on, for their links. */
  deskId: string | null;
  /** Whether the reader may make tickets on this Team; false shows the list alone. */
  canCreate: boolean;
  onCreate: (title: string) => Promise<{ id: string; identifier: string }>;
  onOpen: (id: string) => void;
}

type RowState =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "made"; id: string; identifier: string }
  | { kind: "failed"; message: string };

export function PlanFollowUps({
  pageContent,
  deskId,
  canCreate,
  onCreate,
  onOpen,
}: PlanFollowUpsProps) {
  const followUps = readPlanFollowUpsSection(pageContent);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  if (followUps.length === 0) return null;

  const create = async (title: string) => {
    setRows((prev) => ({ ...prev, [title]: { kind: "busy" } }));
    try {
      const ticket = await onCreate(title);
      setRows((prev) => ({ ...prev, [title]: { kind: "made", ...ticket } }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "That did not go through.";
      setRows((prev) => ({ ...prev, [title]: { kind: "failed", message } }));
    }
  };

  return (
    <Fold
      className="followups"
      title={
        <>
          Found along the way <span className="aside">not part of this plan</span>
        </>
      }
      count={followUps.length}
    >
      <ul className="fu-list" data-testid="plan-follow-ups">
        {followUps.map((followUp) => {
          const state = rows[followUp.title] ?? { kind: "idle" };
          return (
            <li key={followUp.title} className="fu-row" data-testid="plan-follow-up">
              <div className="fu-text">
                <strong>{followUp.title}</strong>
                {followUp.repository ? (
                  <Chip className="fu-repo">{followUp.repository}</Chip>
                ) : null}
                {followUp.why ? <span className="fu-why">{followUp.why}</span> : null}
                {state.kind === "failed" ? (
                  <span className="fu-error" role="alert">
                    {state.message}
                  </span>
                ) : null}
              </div>
              <div className="fu-act">
                {state.kind === "made" ? (
                  <a
                    className="fu-key"
                    href={workItemUrl(state.id, deskId)}
                    onClick={(event) => {
                      event.preventDefault();
                      onOpen(state.id);
                    }}
                  >
                    {state.identifier}
                  </a>
                ) : canCreate ? (
                  <Btn
                    variant="quiet"
                    size="sm"
                    disabled={state.kind === "busy"}
                    onClick={() => void create(followUp.title)}
                  >
                    {state.kind === "busy" ? "Creating" : "Create ticket"}
                  </Btn>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Fold>
  );
}
